import React, { FC, FormEventHandler, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Control, Controller, useFormState } from "react-hook-form";
import { MuiTelInput } from "mui-tel-input";
import { Typography } from "@mui/material";

import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  Stack,
  TextField,
} from "@components/base";

export type RegisterProcurementSupplierFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  repeatPassword: string;
  acceptTerms: boolean;
};

export interface RegisterProcurementSupplierForm {
  onSubmit: FormEventHandler<HTMLFormElement>;
  goToPreviousStep: () => void;
  control: Control<RegisterProcurementSupplierFormValues>;
  watch: (name: keyof RegisterProcurementSupplierFormValues) => string;
  isSubmitting: boolean;
}

const RegisterProcurementSupplierForm: FC<RegisterProcurementSupplierForm> = ({
  onSubmit,
  goToPreviousStep,
  control,
  watch,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const { isValid, isLoading } = useFormState({ control });

  return (
    <form onSubmit={onSubmit}>
      <Stack direction="column" rowGap={3}>
        <Grid container spacing={2}>
          <Controller
            control={control}
            name="firstName"
            render={({ field, fieldState: { error } }): ReactElement => (
              <TextField
                {...field}
                error={Boolean(error)}
                helperText={error?.message}
                label={t("field.title.firstName")}
                disabled={isSubmitting}
                fullWidth
              />
            )}
          />
          <Controller
            control={control}
            name="lastName"
            render={({ field, fieldState: { error } }): ReactElement => (
              <TextField
                {...field}
                error={Boolean(error)}
                helperText={error?.message}
                label={t("field.title.lastName")}
                disabled={isSubmitting}
                fullWidth
              />
            )}
          />

          <Controller
            control={control}
            name="email"
            render={({ field, fieldState: { error } }): ReactElement => (
              <TextField
                {...field}
                error={Boolean(error)}
                helperText={error?.message}
                label={t("field.title.emailAddress")}
                type="email"
                disabled={isSubmitting}
                fullWidth
              />
            )}
          />

          <Controller
            control={control}
            name="phone"
            render={({ field, fieldState: { error } }) => (
              <MuiTelInput
                {...field}
                defaultCountry="CZ"
                error={Boolean(error)}
                helperText={error?.message}
                fullWidth
                variant="filled"
              />
            )}
          />

          <Controller
            control={control}
            name="password"
            rules={{ required: t("validation.password") }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                error={Boolean(error)}
                helperText={error?.message}
                label={t("field.title.password")}
                type="password"
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />

          <Controller
            control={control}
            name="repeatPassword"
            rules={{
              required: t("validation.repeatPassword"),
              validate: (value) =>
                value === watch("password") || t("validation.passwordsMustMatch"),
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                error={Boolean(error)}
                helperText={error?.message}
                label={t("field.title.repeatPassword")}
                type="password"
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />

          <Controller
            control={control}
            name="acceptTerms"
            rules={{
              required: t("validation.error.acceptTheTerms"),
            }}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                  />
                }
                label={
                  <Typography>
                    {t("supplier.registration.agreeWith")}{" "}
                    <Link
                      href="https://www.greenometer.com/greenometer-platform-terms-of-use"
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                    >
                      {t("supplier.registration.termsAndConditions")}
                    </Link>
                  </Typography>
                }
              />
            )}
          />

          <Stack direction="row" spacing={2} width={"100%"}>
            <Button
              size="medium"
              type="button"
              variant="outlined"
              onClick={() => goToPreviousStep()}
              fullWidth
              disabled={isSubmitting || isLoading}
              isLoading={isLoading}
            >
              {t("common.back")}
            </Button>

            <Button
              size="medium"
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSubmitting || isLoading || !isValid}
              isLoading={isSubmitting || isLoading}
            >
              {t("common.start")}
            </Button>
          </Stack>
        </Grid>
      </Stack>
    </form>
  );
};

export default RegisterProcurementSupplierForm;
